import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  DollarSign,
  TrendingUp,
  Mail,
  Phone,
  Linkedin,
  FileText,
  Calendar,
  MessageSquare,
  UserX,
  Building2,
  FolderKanban,
  Users,
  Trash2,
  MapPin,
} from 'lucide-react';
import { pipelineAPI, accountsAPI, contactsAPI, activitiesAPI } from '@/lib/api';
import { useContactTypes } from '@/lib/useContactTypes';
import { ContactDetailSheet } from '@/components/pipeline/ContactDetailSheet';
import { listTeamRoster } from '@/lib/auth';
import { toast } from 'sonner';
import { useProject } from '@/lib/ProjectContext';
import { safeFormat } from '@/lib/utils';
import { useT } from '@/lib/LanguageContext';

// ── Stage colour registry (fallback for stages without a project colour) ───────

const STAGE_FALLBACK_COLORS = {
  new_lead:       { bg: '#DCFCE7', text: '#15803D', border: '#86EFAC' },
  contacted:      { bg: '#DBEAFE', text: '#1D4ED8', border: '#93C5FD' },
  engaged:        { bg: '#EDE9FE', text: '#6D28D9', border: '#D99A90' },
  demo_scheduled: { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
  proposal_sent:  { bg: '#FCE7F3', text: '#BE185D', border: '#F9A8D4' },
  won:            { bg: '#DCFCE7', text: '#15803D', border: '#86EFAC' },
  lost:           { bg: '#F1ECE3', text: '#64748B', border: '#D3CCBE' },
  // project-specific stage ids fall through to the project colour lookup
};

const DEFAULT_STAGES = [
  { id: 'new_lead', name: 'New Lead' },
  { id: 'contacted', name: 'Contacted' },
  { id: 'engaged', name: 'Engaged' },
  { id: 'demo_scheduled', name: 'Demo Scheduled' },
  { id: 'proposal_sent', name: 'Proposal Sent' },
  { id: 'won', name: 'Won' },
  { id: 'lost', name: 'Lost' },
];

function stageBadgeStyle(stageId, projectStages = []) {
  const projectStage = projectStages.find(s => s.id === stageId);
  if (projectStage?.color) {
    return { backgroundColor: projectStage.color + '22', color: projectStage.color, border: `1px solid ${projectStage.color}55` };
  }
  const fallback = STAGE_FALLBACK_COLORS[stageId];
  if (fallback) {
    return { backgroundColor: fallback.bg, color: fallback.text, border: `1px solid ${fallback.border}` };
  }
  return { backgroundColor: '#F1ECE3', color: '#64748B', border: '1px solid #D3CCBE' };
}

function stageName(stageId, projectStages = []) {
  const found = projectStages.find(s => s.id === stageId);
  if (found) return found.name;
  return stageId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Rep avatar ────────────────────────────────────────────────────────────────

function RepAvatar({ assignedTo, teamMap }) {
  const t = useT();
  const member = assignedTo ? teamMap[assignedTo] : null;

  // If no member found (unassigned or stale value), show a clear unassigned state
  if (!member) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 border border-dashed border-[#D3CCBE] bg-[#FBF8F2]">
          <UserX className="w-3.5 h-3.5 text-[#D3CCBE]" />
        </div>
        <span className="text-xs text-[#D3CCBE] hidden sm:block">{t('common.unassigned')}</span>
      </div>
    );
  }

  const name = member.full_name || assignedTo;
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const colors = ['#A62B1F', '#A62B1F', '#4C6B82', '#10B981', '#F59E0B', '#EC4899'];
  const colorIdx = (assignedTo || '').charCodeAt(0) % colors.length;
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-7 w-7 flex-shrink-0">
        <AvatarFallback className="text-white text-xs font-medium" style={{ backgroundColor: colors[colorIdx] }}>
          {initials}
        </AvatarFallback>
      </Avatar>
      <span className="text-xs text-[#5C6670] truncate hidden sm:block">{member.full_name.split(' ')[0]}</span>
    </div>
  );
}

// ── Currency formatter ────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

// ── Deal name helper (accounts are the primary entity) ───────────────────────

function dealName(row) {
  return row.name || row.company || row.full_name || '—';
}

// ── Sort helper ───────────────────────────────────────────────────────────────

function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    let av = a[key] ?? '';
    let bv = b[key] ?? '';
    if (key === 'deal_value') { av = av || 0; bv = bv || 0; }
    if (key === 'dealName') { av = dealName(a); bv = dealName(b); }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Opportunities() {
  const { projects } = useProject();

  const [pipelineData, setPipelineData] = useState([]); // [{ project, accounts, standalone_contacts }]
  const [teamMap, setTeamMap] = useState({});            // email → member
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('dealName');
  const [sortDir, setSortDir] = useState('asc');
  const [groupBy, setGroupBy] = useState('project'); // 'project' | 'company'

  const t = useT();

  const { colorFor, labelFor } = useContactTypes();

  // Edit sheet (editContact is now an account)
  const [editContact, setEditContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editDealValue, setEditDealValue] = useState('');
  const [editStage, setEditStage] = useState('');
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [logType, setLogType] = useState(null);
  const [logNote, setLogNote] = useState('');
  const [loggingActivity, setLoggingActivity] = useState(false);

  // ── Multi-select & bulk delete (rows are accounts or standalone contacts) ──
  const [selectedRows, setSelectedRows] = useState(new Map()); // id -> row
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);

  const toggleSelectRow = (row) => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      next.has(row._id) ? next.delete(row._id) : next.set(row._id, row);
      return next;
    });
  };

  const toggleSelectAllIn = (rowsToToggle) => {
    setSelectedRows((prev) => {
      const allSelected = rowsToToggle.length > 0 && rowsToToggle.every(r => prev.has(r._id));
      const next = new Map(prev);
      if (allSelected) {
        rowsToToggle.forEach(r => next.delete(r._id));
      } else {
        rowsToToggle.forEach(r => next.set(r._id, r));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedRows(new Map());

  const handleBulkDelete = async () => {
    setBulkWorking(true);
    let accountsDeleted = 0;
    let contactsDeleted = 0;
    for (const row of selectedRows.values()) {
      try {
        if (row._isStandalone) {
          await contactsAPI.delete(row._id);
          contactsDeleted++;
          continue;
        }
        for (const c of row._contacts || []) {
          try { await contactsAPI.delete(c._id); contactsDeleted++; } catch (_) {}
        }
        for (const b of row._branches || []) {
          for (const c of b.contacts || []) {
            try { await contactsAPI.delete(c._id); contactsDeleted++; } catch (_) {}
          }
          try { await accountsAPI.delete(b.account._id); accountsDeleted++; } catch (_) {}
        }
        await accountsAPI.delete(row._id);
        accountsDeleted++;
      } catch (_) {}
    }
    clearSelection();
    setBulkDeleteOpen(false);
    setBulkWorking(false);
    toast.success(`Deleted ${accountsDeleted} account${accountsDeleted !== 1 ? 's' : ''} and ${contactsDeleted} contact${contactsDeleted !== 1 ? 's' : ''}`);
    load();
  };

  // ── Quick (inline, single-row) stage / rep updates ─────────────────────────

  const quickUpdateStage = async (row, newStage, silent = false) => {
    try {
      if (row._isStandalone) {
        await pipelineAPI.moveStage(row._id, newStage);
      } else {
        await accountsAPI.update(row._id, { stage: newStage });
      }
      setPipelineData(prev => prev.map(({ project, accounts, standalone_contacts }) => ({
        project,
        accounts: (accounts || []).map(a => a.account._id === row._id ? { ...a, account: { ...a.account, stage: newStage } } : a),
        standalone_contacts: (standalone_contacts || []).map(c => c._id === row._id ? { ...c, stage: newStage } : c),
      })));
      if (!silent) toast.success('Stage updated');
    } catch {
      if (!silent) toast.error('Failed to update stage');
    }
  };

  const quickUpdateRep = async (row, email, silent = false) => {
    try {
      if (row._isStandalone) {
        await contactsAPI.update(row._id, { assigned_to: email });
      } else {
        await accountsAPI.update(row._id, { assigned_to: email });
      }
      setPipelineData(prev => prev.map(({ project, accounts, standalone_contacts }) => ({
        project,
        accounts: (accounts || []).map(a => a.account._id === row._id ? { ...a, account: { ...a.account, assigned_to: email } } : a),
        standalone_contacts: (standalone_contacts || []).map(c => c._id === row._id ? { ...c, assigned_to: email } : c),
      })));
      if (!silent) toast.success('Rep updated');
    } catch {
      if (!silent) toast.error('Failed to update rep');
    }
  };

  // ── Bulk assign rep / move stage ────────────────────────────────────────────

  const [bulkRepOpen, setBulkRepOpen] = useState(false);
  const [bulkRepEmail, setBulkRepEmail] = useState('');
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkStageValue, setBulkStageValue] = useState('');

  const handleBulkAssignRep = async () => {
    if (!bulkRepEmail) return;
    setBulkWorking(true);
    for (const row of selectedRows.values()) {
      try { await quickUpdateRep(row, bulkRepEmail, true); } catch (_) {}
    }
    setBulkRepOpen(false);
    setBulkRepEmail('');
    setBulkWorking(false);
    clearSelection();
    toast.success('Rep assigned');
  };

  const handleBulkMoveStage = async () => {
    if (!bulkStageValue) return;
    setBulkWorking(true);
    for (const row of selectedRows.values()) {
      try { await quickUpdateStage(row, bulkStageValue, true); } catch (_) {}
    }
    setBulkStageOpen(false);
    setBulkStageValue('');
    setBulkWorking(false);
    clearSelection();
    toast.success('Stage updated');
  };

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = async () => {
    setLoading(true);
    try {
      const [pipeRes, teamRes] = await Promise.all([
        pipelineAPI.get(selectedProjectId !== 'all' ? { project_id: selectedProjectId } : {}),
        listTeamRoster().catch(() => []),
      ]);
      setPipelineData(pipeRes.data || []);
      const members = Array.isArray(teamRes) ? teamRes : [];
      const map = {};
      members.forEach(m => {
        if (m.id) map[m.id] = m;
        if (m.email) map[m.email] = m;
      });
      setTeamMap(map);
      setTeamMembers(members);
    } catch (err) {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedProjectId]);

  // ── Derived flat rows (one row per account) ───────────────────────────────

  const rows = useMemo(() => {
    const flat = [];
    pipelineData.forEach(({ project, accounts = [], standalone_contacts = [] }) => {
      accounts.forEach(({ account, contacts, branches }) => {
        flat.push({ ...account, _contacts: contacts || [], _branches: branches || [], _project: project });
      });
      // Standalone contacts (no account) become their own rows using full_name as name
      standalone_contacts.forEach(c => {
        flat.push({ ...c, name: c.full_name, _contacts: [], _project: project, _isStandalone: true });
      });
    });

    const q = search.toLowerCase().trim();
    const filtered = q
      ? flat.filter(r =>
          dealName(r).toLowerCase().includes(q) ||
          r.name?.toLowerCase().includes(q) ||
          r.stage?.toLowerCase().includes(q) ||
          r._contacts?.some(c => c.full_name?.toLowerCase().includes(q))
        )
      : flat;

    return sortRows(filtered, sortKey, sortDir);
  }, [pipelineData, search, sortKey, sortDir]);

  // ── Summary stats ─────────────────────────────────────────────────────────

  const totalValue = rows.reduce((sum, r) => sum + (r.deal_value || 0), 0);
  const wonValue = rows.filter(r => r.stage === 'won').reduce((sum, r) => sum + (r.deal_value || 0), 0);

  // ── Sort toggle ───────────────────────────────────────────────────────────

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-[#A62B1F]" />
      : <ChevronDown className="w-3 h-3 ml-1 text-[#A62B1F]" />;
  };

  // ── Edit sheet ────────────────────────────────────────────────────────────

  const openEdit = async (row) => {
    setEditContact(row);
    setEditDealValue(row.deal_value != null ? String(row.deal_value) : '');
    setEditStage(row.stage || '');
    setLogType(null);
    setLogNote('');
    setActivities([]);
    try {
      const actRes = row._isStandalone
        ? await activitiesAPI.getByContact(row._id)
        : await activitiesAPI.getByAccount(row._id);
      setActivities(actRes.data || []);
    } catch {}
  };

  const closeEdit = () => {
    setEditContact(null);
    setLogType(null);
    setLogNote('');
    setActivities([]);
  };

const submitLog = async () => {
    if (!logType || !editContact) return;
    setLoggingActivity(true);
    try {
      const payload = {
        type: logType,
        body: logNote.trim() || undefined,
        direction: 'outbound',
        performed_by: editContact.assigned_to || 'staff',
      };
      if (editContact._isStandalone) {
        payload.contact_id = editContact._id;
      } else {
        payload.account_id = editContact._id;
      }
      const res = await activitiesAPI.create(payload);
      setActivities(prev => [res.data, ...prev]);
      setLogType(null);
      setLogNote('');
      toast.success('Activity logged');
    } catch {
      toast.error('Failed to log activity');
    } finally {
      setLoggingActivity(false);
    }
  };

  const saveEdit = async () => {
    if (!editContact) return;
    setSaving(true);
    try {
      const updates = { stage: editStage };
      const val = parseFloat(editDealValue);
      updates.deal_value = isNaN(val) ? null : val;

      if (editContact._isStandalone) {
        // standalone contact — move via pipeline endpoint
        await pipelineAPI.moveStage(editContact._id, editStage);
      } else {
        // account — update directly
        await accountsAPI.update(editContact._id, updates);
      }

      // Optimistic update
      setPipelineData(prev =>
        prev.map(({ project, accounts, standalone_contacts }) => ({
          project,
          accounts: (accounts || []).map(({ account, contacts }) =>
            account._id === editContact._id
              ? { account: { ...account, ...updates }, contacts }
              : { account, contacts }
          ),
          standalone_contacts: (standalone_contacts || []).map(c =>
            c._id === editContact._id ? { ...c, stage: editStage } : c
          ),
        }))
      );
      toast.success('Opportunity updated');
      setEditContact(null);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Project stages for the currently selected account/contact ────────────

  const editProjectStages = editContact?._project?.pipeline_stages || [];

  // ── Th helper ─────────────────────────────────────────────────────────────

  const Th = ({ col, label, className = '' }) => (
    <TableHead
      className={`cursor-pointer select-none whitespace-nowrap ${className}`}
      onClick={() => toggleSort(col)}
    >
      <span className="flex items-center text-xs font-semibold text-[#5C6670] uppercase tracking-wide">
        {label}
        <SortIcon col={col} />
      </span>
    </TableHead>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout title="Opportunities">
      <div className="flex flex-col h-full" style={{ fontFamily: 'Manrope, sans-serif' }}>

        {/* ── Header bar ─────────────────────────────────────────────────── */}
        <div
          className="border-b px-6 py-4 flex flex-col gap-4"
          style={{ borderColor: '#E3DDD1', backgroundColor: '#fff' }}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-[#1E2A33]">{t('opportunities.title')}</h1>
              <p className="text-sm text-[#5C6670] mt-0.5">
                {rows.length} account{rows.length !== 1 ? 's' : ''}
                {totalValue > 0 && ` · ${fmtCurrency(totalValue)} total pipeline`}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Project filter */}
              <Select value={selectedProjectId} onValueChange={(v) => { setSelectedProjectId(v); if (v !== 'all') setGroupBy('project'); }}>
                <SelectTrigger className="h-9 w-[180px] text-sm border-[#E3DDD1]">
                  <SelectValue placeholder={t('opportunities.all_projects')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('opportunities.all_projects')}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p._id} value={p._id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: p.color || '#9AA3AB' }}
                        />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Group by toggle */}
              <div className="flex rounded-lg border border-[#E3DDD1] overflow-hidden h-9">
                <button
                  onClick={() => setGroupBy('project')}
                  className={`flex items-center gap-1.5 px-3 text-xs font-medium transition-colors ${groupBy === 'project' ? 'bg-[#A62B1F] text-white' : 'bg-white text-[#5C6670] hover:bg-[#FBF8F2]'}`}
                >
                  <FolderKanban className="w-3.5 h-3.5" /> Project
                </button>
                <button
                  onClick={() => { setGroupBy('company'); setSelectedProjectId('all'); }}
                  className={`flex items-center gap-1.5 px-3 text-xs font-medium transition-colors border-l border-[#E3DDD1] ${groupBy === 'company' ? 'bg-[#A62B1F] text-white' : 'bg-white text-[#5C6670] hover:bg-[#FBF8F2]'}`}
                >
                  <Building2 className="w-3.5 h-3.5" /> Flat
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9AA3AB]" />
                <Input
                  placeholder={t('opportunities.search')}
                  className="pl-8 h-9 w-[200px] text-sm border-[#E3DDD1]"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Summary stats */}
          {totalValue > 0 && (
            <div className="flex gap-4 flex-wrap">
              <div className="flex items-center gap-2 bg-[#FBF8F2] rounded-lg px-3 py-2">
                <DollarSign className="w-4 h-4 text-[#A62B1F]" />
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#9AA3AB] font-semibold">Total Pipeline</p>
                  <p className="text-sm font-bold text-[#1E2A33]">{fmtCurrency(totalValue)}</p>
                </div>
              </div>
              {wonValue > 0 && (
                <div className="flex items-center gap-2 bg-[#F0FDF4] rounded-lg px-3 py-2">
                  <TrendingUp className="w-4 h-4 text-[#10B981]" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#9AA3AB] font-semibold">Closed Won</p>
                    <p className="text-sm font-bold text-[#15803D]">{fmtCurrency(wonValue)}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bulk action bar */}
          {selectedRows.size > 0 && (
            <div className="flex items-center gap-3 bg-[#F8EFED] border border-[#D99A90] rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-[#A62B1F]">{selectedRows.size} selected</span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => { setBulkRepEmail(''); setBulkRepOpen(true); }}
              >
                Assign Rep
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => { setBulkStageValue(''); setBulkStageOpen(true); }}
              >
                Move Stage
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[#5C6670]" onClick={clearSelection}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-[#9AA3AB] text-sm">
              {t('opportunities.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#9AA3AB]">
              <p className="text-sm font-medium">{t('opportunities.none')}</p>
              <p className="text-xs mt-1">Add contacts to the pipeline to see them here</p>
            </div>
          ) : (
            <>
              {groupBy === 'company' ? (
                // ── Flat list (all accounts across projects) ──────────────
                <ProjectSection
                  project={{ name: 'All Accounts', _id: 'all', pipeline_stages: [] }}
                  contacts={rows}
                  teamMap={teamMap}
                  onRowClick={openEdit}
                  Th={Th}
                  toggleSort={toggleSort}
                  SortIcon={SortIcon}
                  hideHeader={false}
                  selectedRows={selectedRows}
                  onToggleRow={toggleSelectRow}
                  onToggleSelectAll={toggleSelectAllIn}
                  onQuickStage={quickUpdateStage}
                  onQuickRep={quickUpdateRep}
                  teamMembers={teamMembers}
                />
              ) : selectedProjectId === 'all' ? (
                // ── Group by project (all projects) ──────────────────────
                pipelineData
                  .filter(({ accounts = [], standalone_contacts = [] }) => {
                    const q = search.toLowerCase().trim();
                    const allRows = [
                      ...accounts.map(({ account, contacts }) => ({ ...account, _contacts: contacts, _project: null })),
                      ...standalone_contacts.map(c => ({ ...c, name: c.full_name, _contacts: [] })),
                    ];
                    return allRows.some(r =>
                      !q ||
                      dealName(r).toLowerCase().includes(q) ||
                      r.stage?.toLowerCase().includes(q)
                    );
                  })
                  .map(({ project, accounts = [], standalone_contacts = [] }) => {
                    const q = search.toLowerCase().trim();
                    const allRows = [
                      ...accounts.map(({ account, contacts, branches }) => ({ ...account, _contacts: contacts || [], _branches: branches || [], _project: project })),
                      ...standalone_contacts.map(c => ({ ...c, name: c.full_name, _contacts: [], _project: project, _isStandalone: true })),
                    ];
                    const filtered = q
                      ? allRows.filter(r =>
                          dealName(r).toLowerCase().includes(q) ||
                          r.stage?.toLowerCase().includes(q) ||
                          r._contacts?.some(c => c.full_name?.toLowerCase().includes(q))
                        )
                      : allRows;
                    const sorted = sortRows(filtered, sortKey, sortDir);
                    if (sorted.length === 0) return null;
                    return (
                      <ProjectSection
                        key={project._id}
                        project={project}
                        contacts={sorted}
                        teamMap={teamMap}
                        onRowClick={openEdit}
                        Th={Th}
                        toggleSort={toggleSort}
                        SortIcon={SortIcon}
                        selectedRows={selectedRows}
                        onToggleRow={toggleSelectRow}
                        onToggleSelectAll={toggleSelectAllIn}
                  onQuickStage={quickUpdateStage}
                  onQuickRep={quickUpdateRep}
                  teamMembers={teamMembers}
                      />
                    );
                  })
              ) : (
                // ── Single project ────────────────────────────────────────
                pipelineData.map(({ project, accounts = [], standalone_contacts = [] }) => {
                  const q = search.toLowerCase().trim();
                  const allRows = [
                    ...accounts.map(({ account, contacts, branches }) => ({ ...account, _contacts: contacts || [], _branches: branches || [], _project: project })),
                    ...standalone_contacts.map(c => ({ ...c, name: c.full_name, _contacts: [], _project: project, _isStandalone: true })),
                  ];
                  const filtered = q
                    ? allRows.filter(r =>
                        dealName(r).toLowerCase().includes(q) ||
                        r.stage?.toLowerCase().includes(q)
                      )
                    : allRows;
                  const sorted = sortRows(filtered, sortKey, sortDir);
                  return (
                    <ProjectSection
                      key={project._id}
                      project={project}
                      contacts={sorted}
                      teamMap={teamMap}
                      onRowClick={openEdit}
                      Th={Th}
                      toggleSort={toggleSort}
                      SortIcon={SortIcon}
                      hideHeader={false}
                      selectedRows={selectedRows}
                      onToggleRow={toggleSelectRow}
                      onToggleSelectAll={toggleSelectAllIn}
                  onQuickStage={quickUpdateStage}
                  onQuickRep={quickUpdateRep}
                  teamMembers={teamMembers}
                    />
                  );
                })
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Detail Sheet ───────────────────────────────────────────────────── */}
      <Sheet open={!!editContact} onOpenChange={open => { if (!open) closeEdit(); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0">
          {editContact && (
            <>
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-[#F1ECE3]">
                <div className="flex items-center gap-2">
                  {!editContact._isStandalone && <Building2 className="w-5 h-5 text-[#A62B1F] flex-shrink-0" />}
                  <SheetTitle className="text-lg font-bold text-[#1E2A33]">{editContact.name || editContact.full_name}</SheetTitle>
                </div>
                {editContact.website && (
                  <a href={editContact.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#4C6B82] hover:underline mt-0.5 block">{editContact.website}</a>
                )}
                {!editContact._isStandalone && (editContact.main_email || editContact.main_phone) && (
                  <div className="flex flex-col gap-1.5 mt-2">
                    {editContact.main_email && (
                      <a href={`mailto:${editContact.main_email}`} className="flex items-center gap-2 text-sm text-[#A62B1F] hover:underline">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" />{editContact.main_email}
                      </a>
                    )}
                    {editContact.main_phone && (
                      <a href={`tel:${editContact.main_phone}`} className="flex items-center gap-2 text-sm text-[#A62B1F] hover:underline">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />{editContact.main_phone}
                      </a>
                    )}
                  </div>
                )}
                {!editContact._isStandalone && (editContact.street || editContact.city || editContact.state || editContact.postal_code || editContact.country) && (
                  <div className="flex items-center gap-2 text-sm text-[#5C6670] mt-2">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    {[editContact.street, editContact.city, editContact.state, editContact.postal_code, editContact.country].filter(Boolean).join(', ')}
                  </div>
                )}
                {/* Contacts inside this account */}
                {!editContact._isStandalone && editContact._contacts?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-[#9AA3AB] uppercase tracking-wide font-semibold flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Contacts ({editContact._contacts.length})
                    </p>
                    {editContact._contacts.map(c => (
                      <div
                        key={c._id}
                        onClick={() => setSelectedContact(c)}
                        className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-[#F8EFED] hover:border-[#A62B1F] cursor-pointer transition-colors group"
                      >
                        <Avatar className="h-9 w-9 flex-shrink-0">
                          <AvatarFallback className="text-xs font-semibold bg-[#F7ECEA] text-[#A62B1F]">
                            {(c.full_name || '?').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1E2A33] truncate">{c.full_name}</p>
                          {c.position && <p className="text-xs text-[#5C6670] truncate">{c.position}</p>}
                          {c.email && (
                            <p className="text-xs text-[#4C6B82] truncate flex items-center gap-1 mt-0.5">
                              <Mail className="w-3 h-3 flex-shrink-0" />{c.email}
                            </p>
                          )}
                          {c.phone && (
                            <p className="text-xs text-[#5C6670] truncate flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />{c.phone}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#CFC8BB] group-hover:text-[#A62B1F] flex-shrink-0 transition-colors" />
                      </div>
                    ))}
                  </div>
                )}
                {/* Branches / locations of this company */}
                {!editContact._isStandalone && editContact._branches?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-[#9AA3AB] uppercase tracking-wide font-semibold flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" /> Branches ({editContact._branches.length})
                    </p>
                    {editContact._branches.map(({ account: b, contacts: bContacts }) => (
                      <div
                        key={b._id}
                        onClick={() => openEdit({ ...b, _contacts: bContacts || [], _branches: [], _project: editContact._project })}
                        className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-[#F8EFED] hover:border-[#A62B1F] cursor-pointer transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#F8EFED] flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-[#A62B1F]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1E2A33] truncate">{b.name}</p>
                          {(b.street || b.city) && (
                            <p className="text-xs text-[#5C6670] truncate">{[b.street, b.city, b.state].filter(Boolean).join(', ')}</p>
                          )}
                          <p className="text-xs text-[#5C6670]">
                            {(bContacts || []).length} contact{(bContacts || []).length !== 1 ? 's' : ''}
                            {(b.main_email || b.main_phone) && ' · has main contact info'}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#CFC8BB] group-hover:text-[#A62B1F] flex-shrink-0 transition-colors" />
                      </div>
                    ))}
                  </div>
                )}
                {/* Standalone contact info */}
                {editContact._isStandalone && (
                  <div className="flex flex-col gap-1.5 mt-3">
                    {editContact.email && (
                      <a href={`mailto:${editContact.email}`} className="flex items-center gap-2 text-sm text-[#A62B1F] hover:underline">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" />{editContact.email}
                      </a>
                    )}
                    {editContact.phone && (
                      <a href={`tel:${editContact.phone}`} className="flex items-center gap-2 text-sm text-[#A62B1F] hover:underline">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />{editContact.phone}
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 space-y-5">

                {/* Meta row */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-[#9AA3AB] uppercase tracking-wide font-semibold mb-0.5">{t('opportunities.assigned_to')}</p>
                    <p className="text-[#1E2A33] truncate">{teamMap[editContact.assigned_to]?.full_name || editContact.assigned_to || '—'}</p>
                  </div>
                  {!editContact._isStandalone && (
                    <div>
                      <p className="text-xs text-[#9AA3AB] uppercase tracking-wide font-semibold mb-0.5">Contacts</p>
                      <p className="text-[#1E2A33]">{editContact._contacts?.length || 0}</p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Stage + Deal Value */}
                <div>
                  <Label className="mb-1.5 block">{t('opportunities.stage')}</Label>
                  <Select value={editStage} onValueChange={setEditStage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {(editProjectStages.length > 0 ? editProjectStages : DEFAULT_STAGES).map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="deal-value" className="mb-1.5 block">Deal Value</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA3AB] text-sm">$</span>
                    <Input
                      id="deal-value"
                      type="number"
                      min="0"
                      placeholder="0"
                      className="pl-6"
                      value={editDealValue}
                      onChange={e => setEditDealValue(e.target.value)}
                    />
                  </div>
                </div>

                <Button
                  className="w-full bg-[#A62B1F] hover:bg-[#8C2318] text-white"
                  onClick={saveEdit}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : t('common.save')}
                </Button>

                <Separator />

                {/* Log Activity */}
                <div>
                  <p className="text-sm font-semibold text-[#1E2A33] mb-3">{t('pipeline.log_activity')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { type: 'call',     label: t('pipeline.log_call'),    Icon: Phone },
                      { type: 'email',    label: t('pipeline.log_email'),   Icon: Mail },
                      { type: 'linkedin', label: t('pipeline.log_linkedin'),Icon: Linkedin },
                      { type: 'note',     label: t('pipeline.log_note'),    Icon: FileText },
                    ].map(({ type, label, Icon }) => (
                      <button
                        key={type}
                        onClick={() => setLogType(logType === type ? null : type)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all"
                        style={{
                          borderColor: logType === type ? '#A62B1F' : '#E3DDD1',
                          backgroundColor: logType === type ? '#F3F0FF' : '#fff',
                          color: logType === type ? '#A62B1F' : '#5C6670',
                        }}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {logType && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        placeholder="Add notes..."
                        value={logNote}
                        onChange={e => setLogNote(e.target.value)}
                        rows={2}
                        className="text-sm resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-[#A62B1F] hover:bg-[#7c55e0] text-white"
                          onClick={submitLog}
                          disabled={loggingActivity}
                        >
                          {loggingActivity ? 'Saving…' : 'Log'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setLogType(null); setLogNote(''); }}>
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Activity feed */}
                <div>
                  <p className="text-sm font-semibold text-[#1E2A33] mb-3">{t('pipeline.activity')}</p>
                  {activities.length === 0 ? (
                    <p className="text-sm text-[#9AA3AB]">{t('pipeline.no_activities')}</p>
                  ) : (
                    <div className="space-y-3">
                      {activities.map(act => {
                        const iconMap = { call: Phone, email: Mail, linkedin: Linkedin, note: FileText, meeting: Calendar };
                        const Icon = iconMap[act.type] || MessageSquare;
                        return (
                          <div key={act._id} className="flex gap-3">
                            <div className="w-7 h-7 rounded-full bg-[#F3F0FF] flex items-center justify-center flex-shrink-0">
                              <Icon className="w-3.5 h-3.5 text-[#A62B1F]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-[#5C6670] capitalize">{act.type}</p>
                              {act.notes && <p className="text-sm text-[#1E2A33] mt-0.5">{act.notes}</p>}
                              <p className="text-xs text-[#9AA3AB] mt-0.5">{safeFormat(act.created_at, 'MMM d, yyyy')}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Contact drill-down sheet */}
      {selectedContact && (
        <ContactDetailSheet
          contact={selectedContact}
          stages={editProjectStages}
          isOpen={!!selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={load}
          teamMap={teamMap}
        />
      )}

      {/* Bulk assign rep */}
      <AlertDialog open={bulkRepOpen} onOpenChange={setBulkRepOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign rep to {selectedRows.size} selected</AlertDialogTitle>
            <AlertDialogDescription>Choose a team member to assign.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={bulkRepEmail} onValueChange={setBulkRepEmail}>
              <SelectTrigger>
                <SelectValue placeholder="Choose rep" />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (
                  <SelectItem key={m.email} value={m.email}>
                    {m.full_name || m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkAssignRep} disabled={bulkWorking || !bulkRepEmail}>
              {bulkWorking ? 'Assigning…' : 'Assign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk move stage */}
      <AlertDialog open={bulkStageOpen} onOpenChange={setBulkStageOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move {selectedRows.size} selected to a stage</AlertDialogTitle>
            <AlertDialogDescription>Applies to accounts and standalone contacts alike.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={bulkStageValue} onValueChange={setBulkStageValue}>
              <SelectTrigger>
                <SelectValue placeholder="Choose stage" />
              </SelectTrigger>
              <SelectContent>
                {DEFAULT_STAGES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkMoveStage} disabled={bulkWorking || !bulkStageValue}>
              {bulkWorking ? 'Updating…' : 'Move'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedRows.size} selected?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes each selected account along with its contacts and branches (or the standalone contact, for standalone rows). This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkWorking}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkWorking ? 'Deleting…' : `Delete ${selectedRows.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

// ── ProjectSection — table for one project (rows are accounts) ───────────────

function ProjectSection({ project, contacts: rows, teamMap, onRowClick, Th, hideHeader = false, selectedRows, onToggleRow, onToggleSelectAll, onQuickStage, onQuickRep, teamMembers = [] }) {
  const t = useT();
  if (!rows || rows.length === 0) return null;
  const stages = project.pipeline_stages?.length > 0 ? project.pipeline_stages : DEFAULT_STAGES;
  const total = rows.reduce((s, r) => s + (r.deal_value || 0), 0);
  const allSelected = rows.length > 0 && rows.every(r => selectedRows?.has(r._id));

  return (
    <div className="mb-1">
      {/* Section header */}
      {!hideHeader && (
        <div
          className="flex items-center gap-3 px-6 py-2.5 sticky top-0 z-10"
          style={{ backgroundColor: '#FBF8F2', borderBottom: '1px solid #E3DDD1' }}
        >
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: project.color || '#9AA3AB' }}
          />
          <span className="text-xs font-bold text-[#5C6670] uppercase tracking-widest">
            {project.name}
          </span>
          <span className="text-xs text-[#9AA3AB]">({rows.length})</span>
          <div className="flex-1" />
          {total > 0 && (
            <span className="text-xs font-semibold text-[#5C6670]">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(total)}
            </span>
          )}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow className="bg-white border-b border-[#F1ECE3]">
            <TableHead className="pl-6 w-8">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => onToggleSelectAll?.(rows)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Select all"
              />
            </TableHead>
            <Th col="dealName" label="Account" />
            <Th col="assigned_to" label="Rep" />
            <Th col="_contacts" label="Contacts" />
            <Th col="stage" label={t('opportunities.stage')} />
            <Th col="deal_value" label={t('opportunities.value')} className="text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => {
          const rowStages = row._project?.pipeline_stages?.length > 0 ? row._project.pipeline_stages : stages;
          return (
            <TableRow
              key={row._id}
              className="cursor-pointer hover:bg-[#FBF8F2] transition-colors border-b border-[#F1ECE3]"
              onClick={() => onRowClick(row)}
            >
              {/* Select */}
              <TableCell className="pl-6 py-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedRows?.has(row._id) || false}
                  onCheckedChange={() => onToggleRow?.(row)}
                  aria-label="Select row"
                />
              </TableCell>

              {/* Account name */}
              <TableCell className="py-3">
                <div className="flex items-center gap-1.5">
                  {!row._isStandalone && <Building2 className="w-3.5 h-3.5 text-[#A62B1F] flex-shrink-0" />}
                  <span className="text-sm font-semibold text-[#1E2A33]">{dealName(row)}</span>
                  {row._branches?.length > 0 && (
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal text-[#A62B1F] border-[#D99A90]">
                      {row._branches.length} branch{row._branches.length !== 1 ? 'es' : ''}
                    </Badge>
                  )}
                </div>
              </TableCell>

              {/* Rep — inline editable */}
              <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                <Select value={row.assigned_to || ''} onValueChange={(v) => onQuickRep?.(row, v)}>
                  <SelectTrigger className="h-7 w-[130px] text-xs border-transparent hover:border-[#E3DDD1] px-1.5">
                    {row.assigned_to ? (
                      <RepAvatar assignedTo={row.assigned_to} teamMap={teamMap} />
                    ) : (
                      <span className="text-xs text-[#D3CCBE]">Unassigned</span>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.email} value={m.email}>
                        {m.full_name || m.name || m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>

              {/* Contacts count */}
              <TableCell className="py-3">
                {row._isStandalone ? (
                  <span className="text-xs text-[#9AA3AB]">—</span>
                ) : (
                  <span className="text-xs text-[#5C6670]">{row._contacts?.length || 0} contact{(row._contacts?.length || 0) !== 1 ? 's' : ''}</span>
                )}
              </TableCell>

              {/* Stage — inline editable */}
              <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                <Select value={row.stage || ''} onValueChange={(v) => onQuickStage?.(row, v)}>
                  <SelectTrigger
                    className="h-7 w-[150px] text-xs font-semibold rounded-full border-0 focus:ring-0"
                    style={stageBadgeStyle(row.stage, rowStages)}
                  >
                    <SelectValue>{stageName(row.stage, rowStages)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {rowStages.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>

              {/* Deal value */}
              <TableCell className="py-3 text-right">
                <span className="text-sm font-semibold text-[#1E2A33]">
                  {row.deal_value != null
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(row.deal_value)
                    : <span className="text-[#D3CCBE] font-normal">—</span>
                  }
                </span>
              </TableCell>
            </TableRow>
          );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
